import * as PopperJS from "@popperjs/core";
import classNames from "classnames";
import { useCombobox } from "downshift";
import React, { AriaAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useElementId } from "../../hooks/use-element-id";
import { IconButton } from "../button/icon-button";
import { IconNavArrowDown, IconNavArrowUp } from "../icons";
import { Input, InputProps } from "../input";
import { Popover } from "../popover/popover";
import { PopoverContentProps, PopoverProps } from "../popover/popover.types";
import { MultiSelectTokenList } from "./multi-select-token-list";
import { CSS_CLASSES } from "./select-css-classes";
import { SelectOptionsList } from "./select-options-list";
import { SelectOptionsListItemProps } from "./select-options-list-item";
import {
  filterOptions,
  filterOptionsGroups,
  flattenOptions,
  isListOfOptions,
  isListOfOptionsGroups,
} from "./select-utils";
import {
  AutoCompleteFilterPredicate,
  OptionsGroup,
  OptionsToString,
  SelectHandler,
  SelectOption,
} from "./select.types";

export interface SelectAnchorProps {
  open: boolean;
  toggleButtonProps: any;
}

export type SelectVariant = "form-control" | "ui-element";

export type AutoCompleteFilterMode = "always" | "on-input-only";

export interface SelectOwnProps<S extends SelectOption> {
  /**
   * `form-control` : This is the default variant in which the select component behaves like a regular form control.
   *
   * `ui-element`   : Use this variant if you want to allow user to select an item from a list where you want an
   *  arbitrary UI Element to act as the anchor for the drop-down list, instead of a form control.
   *
   * @default form-control
   *
   * @todo: deprecate: Replace with separate `SelectionMenu` component.
   */
  variant?: SelectVariant;

  /**
   * This props, when set to `true`, enable selection of multiple items.
   *
   * @default false
   */
  multiSelect?: boolean;

  /**
   * This prop, when set to `true`, enables filtering of the options list based on text types in an text input field
   *
   * @default false
   *
   * @todo: deprecate: Replaced by separate `SingleSelectCombobox` and `MultiSelectCombobox` components.
   */
  autoComplete?: boolean;

  /**
   * This prop controls the filtering behavior when in `autoCOmplete` mode.
   *
   * `always`        : The options list is filtered based on the text being actively typed in the text input field as
   * well as the currently selected value (if any).
   *
   * `on-input-only` : The options list is only filtered based on the text being actively typed in the text input field.
   * Other than that the list shows all available options.
   *
   * @default always
   *
   * @todo: deprecate: ComboBox-only prop. This is non standard UX. Replaced by `filter`/`highlight` prop in new `*ComboBox` components.
   */
  autoCompleteFilterMode?: AutoCompleteFilterMode;

  /**
   * This prop, when set to `true`, this feature will make custom text from input field as part of option list and
   * this parameter will only take effect if `autoComplete` parameter is set to `true`.
   *
   * @default false
   *
   * @todo: deprecate: ComboBox-only prop. This is non standard UX. Replaced by `selectOnly` prop in new `*ComboBox` components.
   */
  allowCustomInputAsOption?: boolean;

  /**
   * A predicate function that can be used to customize the logic for filtering options from the list based on the
   * value being typed in the text input field, when `autoComplete=true`.
   *
   * A default predicate function is provided with does a case-insensitive "begins-with" test as the filtering logic.
   *
   * @todo: deprecate: ComboBox-only prop. Rename to `optionMatchPredicate` prop in new `*ComboBox` components.
   */
  autoCompleteFilterPredicate?: AutoCompleteFilterPredicate<S>;

  /**
   * This prop controls whether the options list element is rendered with a width matching that of the select form
   * control element.
   *
   * It is `true` by default when `variant="form-control"` or `variant` is not specified.
   *
   * It is `false` by default when `variant="ui-element"`.
   *
   * @todo: change in behavior: `true` by default always. Needs to be set to `false` if needed. Then use `listboxStyle` prop to set custom width.
   */
  matchWidth?: boolean;

  /**
   * This prop is passed through to the `placement` option of `Popper.js`, and controls the placement of the options
   * list element relative to the select form control element.
   *
   * @default bottom
   *
   * @todo: change in behavior: change default in new components to bottom-start.
   */
  placement?: PopperJS.Placement;

  /**
   * The list of options or options-groups to allow selection from.
   *
   * The option object must conform to the `SelectOption` interface and have a `value` property and can optionally have
   * `id`, `label` and `disabled` properties. The object may have other custom properties.
   *
   * If you want grouped options, then pass a list of OptionsGroup objects instead. These must have a `groupLabel`
   * property and a `groupOptions` property. The value against the `groupOptions` property must be a list of
   * `SelectOption` objects.
   */
  options: S[] | OptionsGroup<S>[];

  /**
   * A function that maps a given option to a string label to be used to display in the options list.
   *
   * By default it will use the `label` property of the option object.
   * If `label` is not available it will use the `value` property, which is a required property of the option object.
   */
  optionToString?: OptionsToString<S>;

  /**
   * Event triggered each time the selection changes.
   *
   * If `multiSelect` is `false` then the handler is called with the selected `SelectOption` object from the list.
   *
   * If `multiSelect` is `true` then the handler is called with a list of  selected `SelectOption` objects.
   *
   * If the selection is cleared, then the handler is called with `null`.
   */
  onSelect: SelectHandler<S>;

  /**
   * This prop accepts the value to be set as the controlled select value.
   * Depending on `multiSelect`, this accepts a `SelectOption` object or a list of `SelectOption` objects.
   *
   * @todo: change in behavior: Must also accept `null` and `undefined` and treat them as unselect all. Standard React runtime error when switching from/to  undefined to non-undefined.
   */
  selected?: S | S[];

  /**
   * Use this prop to pass a custom option list item render function.
   *
   * The function will be called for each option in the `options` list and will be passed the following arguments:
   * `option`         : The current option in the list.
   * `highlighted`    : Indicates if it's currently highlighted via keyboard focus.
   * `optionToString` : The function that was passed to convert option object to string label.
   * `isGroupOption`  : Indicates if this option object is part of group of options.
   *
   * @todo: change in behavior: composable ListboxOptionContent component that handles ALL - aria, highlighted, selected, disabled, grouped
   */
  optionsListItemRenderer?: (props: SelectOptionsListItemProps<S>) => JSX.Element;

  /**
   * This prop controls if a icon button is rendered that acts as the clear button.
   * Clicking on that button clears any currently selected option(s).
   *
   * @default false
   */
  showClearButton?: boolean;

  /**
   * This props controls if the clear button is always shown even if there is no, non-empty value to clear.
   * This supersedes the `showClearButton` prop.
   *
   * @default false
   *
   * @todo: deprecate: This is non standard UX. Will only support `showClearButton`
   */
  alwaysShowClearButton?: boolean;

  /**
   * `className` passed to the root of the `Input` element.
   *
   * @todo: deprecate: Review usage.
   */
  contentClassName?: string;

  /**
   * `className` applied to the `Popover` element.
   */
  className?: string;

  /**
   * Used when `variant="ui-element"` to render the options list popover anchor.
   */
  children?: ((props: SelectAnchorProps) => JSX.Element) | JSX.Element;

  /**
   * Passthrough prop for the underlying Popover component.
   */
  usePortal?: PopoverProps["usePortal"];

  /**
   * Used to determine whether to use the index of an option as its key in a list
   *
   * This prop can be helpful on some cases where it is required to show multiple options that has the same value.
   *
   *  Ex. address list: we want to show the full address (city,street_line, zipcode) but we want just to capture the street_line value
   *
   * If `useOptionIndexAsKey` is false then the option key will use the option value.
   *
   * If `useOptionIndexAsKey` is true then the  option key will use the index value from the `.map()` function
   *
   * @default false
   */
  useOptionIndexAsKey?: boolean;
}

export type SelectToInputPassTroughProps<S extends SelectOption = SelectOption> = Omit<
  InputProps,
  | keyof SelectOwnProps<S>
  | "endEnhancer"
  | "endEnhancerClassName"
  | "className"
  | "value"
  | "readOnly"
  | "onChange"
  | "inputRef"
  | "labelProps"
>;

export type SelectProps<S extends SelectOption = SelectOption> = SelectOwnProps<S> & SelectToInputPassTroughProps<S>;

const defaultOptionToString = <S extends SelectOption>(option: S) => (option ? option.label || option.value : "");

const defaultAutoCompleteFilterPredicate: AutoCompleteFilterPredicate = (filterValue, option: SelectOption) => {
  return option?.value?.toLowerCase().startsWith(filterValue?.toLowerCase());
};

/**
 * The `Select` component represents a form input that allows a user to select from a list of options.
 */
export const Select = <S extends SelectOption = SelectOption>(props: SelectProps<S>) => {
  const {
    /* Select Own Props */
    variant = "form-control",
    multiSelect = false,
    autoComplete = false,
    allowCustomInputAsOption = false,
    autoCompleteFilterMode = "always",
    autoCompleteFilterPredicate,
    matchWidth = props.variant === "form-control" || props.variant === undefined ? true : false,
    placement = "bottom",
    children,
    options,
    optionToString = defaultOptionToString,
    onSelect,
    selected,
    optionsListItemRenderer,
    showClearButton = false,
    alwaysShowClearButton = false,
    contentClassName,
    className,
    useOptionIndexAsKey = false,
    /* Input Pass-through Props */
    id,
    name,
    required,
    disabled,
    textAlign,
    formField,
    formFieldClassName,
    label,
    labelInfo,
    hideLabel,
    secondaryAction,
    color,
    hint,
    success,
    error,
    textLength,
    placeholder,
    onFocus,
    onBlur,
    inputClassName,
    inputBorderClassName,
    /* Popover Pass-through Props */
    usePortal,
    /* Rest props */
    ...restProps
  } = props;

  const formFieldId = useElementId(id, "select");

  const inputRef = useRef<HTMLInputElement>(null);

  const [filteredOptions, setFilteredOptions] = useState<S[] | OptionsGroup<S>[]>(options);
  const [needSetInput, setNeedSetInput] = useState(false);

  const filterPredicate = useMemo(
    () => autoCompleteFilterPredicate || defaultAutoCompleteFilterPredicate,
    [autoCompleteFilterPredicate]
  );

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    getComboboxProps,
    highlightedIndex,
    setInputValue,
    reset,
    selectItem,
    toggleMenu,
    inputValue,
  } = useCombobox<any>({
    id: formFieldId,
    items: isListOfOptionsGroups(filteredOptions) ? flattenOptions(filteredOptions) : filteredOptions,
    initialInputValue: "",
    circularNavigation: true,
    itemToString: optionToString as () => string,
    ...(multiSelect && {
      stateReducer: ({ selectedItem }, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        switch (type) {
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
            return {
              ...changes,
              selectedItem: [...(selectedItem ? selectedItem : []), changes.selectedItem],
              inputValue: "",
              isOpen: true, // keep the menu open after selection.
            };
          case useCombobox.stateChangeTypes.InputBlur:
            return {
              ...changes,
              selectedItem,
            };
        }
        return changes;
      },
    }),
    onSelectedItemChange: ({ selectedItem }) => {
      if (multiSelect) {
        let selectedOptions = options;
        if (selectedItem && isListOfOptions(options)) {
          selectedOptions = filterOptions(options as S[], selectedItem as S[]);
        } else if (selectedItem && isListOfOptionsGroups(options)) {
          selectedOptions = filterOptionsGroups(options as OptionsGroup<S>[], selectedItem as OptionsGroup<S>[]);
        }

        setFilteredOptions(selectedOptions);
      }
      onSelect(selectedItem);
    },
    onStateChange: (data: any) => {
      if (autoComplete && allowCustomInputAsOption && !isListOfOptionsGroups(options) && !multiSelect) {
        if (data?.type === useCombobox.stateChangeTypes.InputBlur && inputValue) {
          const optionForInputValue = (filteredOptions as S[])?.find(
            (option: S) => option?.value?.toLowerCase() === inputValue.toLowerCase()
          );
          if (!selectedItem || (selectedItem && selectedItem.value !== inputValue)) {
            selectItem(optionForInputValue);
          }
        } else if (
          (data?.type === useCombobox.stateChangeTypes.InputBlur && !data?.inputValue) ||
          data?.type === useCombobox.stateChangeTypes.FunctionSetInputValue
        ) {
          reset();
        }
      }
    },
    initialSelectedItem: selected,
    selectedItem: selected,
    ...(autoComplete && {
      onInputValueChange: ({ inputValue, selectedItem }) => {
        let unselectedOptions = [...options] as S[] | OptionsGroup<S>[];
        const groupOptions: boolean = isListOfOptionsGroups(options);
        if (autoComplete && allowCustomInputAsOption && !groupOptions && !multiSelect && inputValue) {
          const optionAlreadyExists =
            unselectedOptions?.findIndex((option: any) => option?.value?.toLowerCase() === inputValue.toLowerCase()) !==
            -1;
          if (!optionAlreadyExists) {
            unselectedOptions.unshift({
              id: "custom-option",
              value: inputValue,
            } as any);
          }
        }
        if (multiSelect && selectedItem) {
          if (!groupOptions) {
            unselectedOptions = filterOptions(options as S[], selectedItem as S[]);
          } else {
            unselectedOptions = filterOptionsGroups(options as OptionsGroup<S>[], selectedItem as OptionsGroup<S>[]);
          }
        }
        if (autoCompleteFilterMode === "on-input-only" && inputValue === selectedItem?.value) {
          setFilteredOptions(unselectedOptions);
        } else {
          if (groupOptions) {
            const filteredItems = (unselectedOptions as OptionsGroup<S>[])
              .map((group) => {
                return {
                  groupLabel: group.groupLabel,
                  groupOptions: group.groupOptions.filter(filterPredicate.bind(undefined, inputValue)),
                };
              })
              .filter((group) => group.groupOptions.length > 0);

            setFilteredOptions(filteredItems);
          } else {
            setFilteredOptions((unselectedOptions as S[]).filter(filterPredicate.bind(undefined, inputValue)));
          }
        }
      },
      onIsOpenChange: ({ isOpen }) => {
        if (!isOpen) {
          setNeedSetInput(true);
        }
      },
    }),
  });
  useEffect(() => {
    if (needSetInput) {
      if (multiSelect) {
        setInputValue("");
      } else {
        setInputValue(optionToString(selectedItem));
      }

      setNeedSetInput(false);
    }
    if (multiSelect) {
      let selectedOptions = options;
      if (selectedItem && isListOfOptions(options)) {
        selectedOptions = filterOptions(options as S[], selectedItem as S[]);
      } else if (selectedItem && isListOfOptionsGroups(options)) {
        selectedOptions = filterOptionsGroups(options as OptionsGroup<S>[], selectedItem as OptionsGroup<S>[]);
      }

      setFilteredOptions(selectedOptions);
    } else if (autoComplete && selectedItem?.value) {
      if (autoCompleteFilterMode === "on-input-only") {
        setFilteredOptions(options);
      } else {
        if (isListOfOptionsGroups(options)) {
          const filteredItems = (options as OptionsGroup<S>[])
            .map((group) => {
              return {
                groupLabel: group.groupLabel,
                groupOptions: group.groupOptions.filter(filterPredicate.bind(undefined, selectedItem?.value)),
              };
            })
            .filter((group) => group.groupOptions.length > 0);

          setFilteredOptions(filteredItems);
        } else {
          setFilteredOptions((options as S[]).filter(filterPredicate.bind(undefined, selectedItem?.value)));
        }
      }
    }
  }, [
    autoComplete,
    autoCompleteFilterMode,
    filterPredicate,
    multiSelect,
    needSetInput,
    optionToString,
    options,
    selectedItem,
    setInputValue,
  ]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const onRemoveTokenHandler = useCallback(
    (option: S) => {
      const newSelectedItemList = (selectedItem as S[]).filter((item) => item.value !== option.value);
      selectItem(newSelectedItemList.length > 0 ? newSelectedItemList : null);
    },
    [selectItem, selectedItem]
  );

  const selectOptionsList = ({ innerRef, anchorElement, style }: PopoverContentProps) => {
    const optionListStyle = matchWidth ? { width: anchorElement?.clientWidth } : {};
    return (
      <SelectOptionsList<S>
        options={filteredOptions}
        highlightedIndex={highlightedIndex}
        color={color}
        getItemProps={getItemProps}
        optionsListItemRenderer={optionsListItemRenderer}
        optionToString={optionToString}
        clampWidth={variant !== "form-control"}
        innerRef={innerRef}
        style={{ ...style, ...optionListStyle }}
        useOptionIndexAsKey={useOptionIndexAsKey}
      />
    );
  };

  const inputAriaAttributesOverride: AriaAttributes = useMemo(
    () =>
      !formField || hideLabel
        ? {
            "aria-label": label,
            "aria-labelledby": undefined,
          }
        : {},
    [formField, hideLabel, label]
  );

  const { value, ...inputProps } = getInputProps({
    refKey: "inputRef",
    placeholder,
    ref: inputRef,
    "aria-autocomplete": autoComplete ? "list" : undefined,
  });

  const inputAnchor = useMemo(
    () => (
      <Input
        /* Pass-through props */
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        textAlign={textAlign}
        formField={formField}
        formFieldClassName={formFieldClassName}
        formFieldType="other"
        label={label}
        labelInfo={labelInfo}
        hideLabel={hideLabel}
        secondaryAction={secondaryAction}
        color={color}
        hint={hint}
        success={success}
        error={error}
        textLength={textLength}
        /* Non-pass-through props */
        {...(multiSelect && {
          tags: (
            <MultiSelectTokenList
              selected={selectedItem as S[]}
              onRemoveToken={onRemoveTokenHandler}
              optionToString={optionToString}
              onClick={toggleMenu}
              color={color}
              disabled={disabled}
            />
          ),
        })}
        showClearButton={showClearButton}
        alwaysShowClearButton={alwaysShowClearButton}
        onClearButtonClick={showClearButton || alwaysShowClearButton ? reset : undefined}
        endEnhancer={({ disabled, focused }) => (
          <IconButton
            ripple={false}
            color={error ? "error" : focused ? color : "gray"}
            disabled={disabled}
            variant="ghost"
            size="small"
            label="toggle combobox"
            icon={
              isOpen ? (
                <IconNavArrowUp color={error ? "error" : focused ? color : "gray"} />
              ) : (
                <IconNavArrowDown color={error ? "error" : focused ? color : "gray"} />
              )
            }
            className={CSS_CLASSES.SELECT_INPUT_ENHANCER_BUTTON}
            {...getToggleButtonProps()}
          />
        )}
        endEnhancerClassName={CSS_CLASSES.SELECT_INPUT_ENHANCER}
        inputClassName={classNames(
          CSS_CLASSES.SELECT_INPUT,
          {
            [CSS_CLASSES.SELECT_INPUT_DISABLED]: disabled,
            [CSS_CLASSES.SELECT_INPUT_PLACEHOLDER]: !selectedItem,
            [CSS_CLASSES.SELECT_INPUT_MULTI]: multiSelect,
          },
          inputClassName
        )}
        inputBorderClassName={inputBorderClassName}
        className={contentClassName}
        labelProps={getLabelProps()}
        rootProps={getComboboxProps({ role: undefined })}
        /* rest props */
        {...restProps}
        /* downshift props */
        {...inputProps}
        value={value || ""}
        {...(!autoComplete && {
          readOnly: true,
          onChange: () => {},
          value: selectedItem ? (multiSelect ? "" : optionToString(selectedItem)) : "",
          ...getToggleButtonProps(inputAriaAttributesOverride),
          tabIndex: 0,
          onFocus: (event) => {
            onFocus?.(event);
            if (inputProps.hasOwnProperty("onFocus") && typeof inputProps.onFocus === "function") {
              inputProps.onFocus(event);
            }
          },
          onBlur: (event) => {
            onBlur?.(event);
            if (inputProps.hasOwnProperty("onBlur") && typeof inputProps.onBlur === "function") {
              inputProps.onBlur(event);
            }
          },
        })}
        {...(autoComplete &&
          !multiSelect && {
            onFocus: (event) => {
              onFocus?.(event);
              if (inputProps.hasOwnProperty("onFocus") && typeof inputProps.onFocus === "function") {
                inputProps.onFocus(event);
              }
              if (selectedItem?.value) {
                setTimeout(() => inputRef?.current?.select(), 100);
              }
            },
            onBlur: (event) => {
              onBlur?.(event);
              if (inputProps.hasOwnProperty("onBlur") && typeof inputProps.onBlur === "function") {
                inputProps.onBlur(event);
              }
            },
          })}
        {...(autoComplete &&
          multiSelect && {
            onFocus: (event) => {
              onFocus?.(event);
              if (inputProps.hasOwnProperty("onFocus") && typeof inputProps.onFocus === "function") {
                inputProps.onFocus(event);
              }
            },
            onBlur: (event) => {
              onBlur?.(event);
              if (inputProps.hasOwnProperty("onBlur") && typeof inputProps.onBlur === "function") {
                inputProps.onBlur(event);
              }
            },
          })}
        {...(multiSelect &&
          selectedItem &&
          selectedItem.length > 0 && {
            placeholder: "",
          })}
      />
    ),
    [
      alwaysShowClearButton,
      autoComplete,
      color,
      contentClassName,
      disabled,
      error,
      formField,
      formFieldClassName,
      getComboboxProps,
      getLabelProps,
      getToggleButtonProps,
      hideLabel,
      hint,
      id,
      inputAriaAttributesOverride,
      inputBorderClassName,
      inputClassName,
      inputProps,
      isOpen,
      label,
      labelInfo,
      multiSelect,
      name,
      onBlur,
      onFocus,
      onRemoveTokenHandler,
      optionToString,
      required,
      reset,
      restProps,
      secondaryAction,
      selectedItem,
      showClearButton,
      success,
      textAlign,
      textLength,
      toggleMenu,
      value,
    ]
  );

  const uiAnchor: JSX.Element | null = useMemo(() => {
    if (children) {
      const anchorProps = {
        ...getComboboxProps({ role: undefined }),
        ...getInputProps({ "aria-autocomplete": undefined }),
        ...getToggleButtonProps(inputAriaAttributesOverride),
        tabIndex: 0,
        role: undefined,
      };
      return typeof children === "function"
        ? children({
            open: isOpen,
            toggleButtonProps: anchorProps,
          })
        : React.cloneElement(children, anchorProps);
    } else {
      return null;
    }
  }, [children, getComboboxProps, getInputProps, getToggleButtonProps, inputAriaAttributesOverride, isOpen]);

  const selectAnchor = useMemo(
    () => (variant === "ui-element" && uiAnchor ? uiAnchor : inputAnchor),
    [inputAnchor, uiAnchor, variant]
  );

  return (
    <Popover
      open={isOpen}
      placement={placement}
      usePortal={usePortal}
      lazyPortal={false}
      overlayRootProps={getMenuProps(
        {},
        // @ts-ignore
        { suppressRefError: true }
      )}
      content={selectOptionsList}
      anchorClassName={
        variant === "form-control" ? CSS_CLASSES.SELECT_POPOVER_ANCHOR_FORM_CONTROL : CSS_CLASSES.SELECT_POPOVER_ANCHOR
      }
      className={className}
    >
      {selectAnchor}
    </Popover>
  );
};
